import { useEffect, useRef, type ReactNode } from 'react';
import type { UserSettings } from '@etown/shared';
import { localMedia } from '../../webrtc/localMedia';
import { useOffice } from '../office/OfficeContext';
import { useSettingsStore } from '../../stores/settingsStore';
import {
  DEFAULT_VIEW_SETTINGS,
  FIELD_OF_VIEW_RANGE,
  QUALITY_RANGE,
  SENSITIVITY_RANGE,
  useViewStore,
} from '../../stores/viewStore';
import { STIL3D_AUSWAHL, STIL3D_STANDARD, stilWaehlbar } from '../../renderer3d/theme/stil3dAuswahl';
import type { ViewSettings } from '../../game/view';
import { istTouchGeraet } from '../../lib/eingabeart';

interface SettingsPopoverProps {
  onClose: () => void;
  /** Die Textlinks der Leiste — auf dem Handy wohnen sie hier als Liste. */
  links?: ReactNode;
}

/**
 * Einstellungs-Popover ueber dem ⚙️-Button: Anklingeln, Audio-Verarbeitung
 * und — in der Ego-Ansicht — Bedienung und Darstellung.
 */
export function SettingsPopover({ onClose, links }: SettingsPopoverProps) {
  const { socket, runtime, mapTheme } = useOffice();
  const settings = useSettingsStore((s) => s.settings);
  const setSettings = useSettingsStore((s) => s.setSettings);
  const viewMode = useViewStore((s) => s.mode);
  const view = useViewStore((s) => s.settings);
  const updateView = useViewStore((s) => s.updateSettings);
  const stil3d = useViewStore((s) => s.stil3d);
  const switching = useViewStore((s) => s.switching);
  const setStil3d = useViewStore((s) => s.setStil3d);
  const setSwitching = useViewStore((s) => s.setSwitching);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [onClose]);

  if (!settings) return null;

  const update = (patch: Partial<UserSettings>): void => {
    // Optimistisch anwenden; Ack-Fehler rollt zurueck
    const previous = settings;
    const next = { ...settings, ...patch };
    setSettings(next);
    localMedia.applyAudioSettings(next);
    socket.emit('settings:update', patch, (res) => {
      if (res.ok) {
        setSettings(res.data);
        localMedia.applyAudioSettings(res.data);
        if (patch.ringRequired !== undefined) {
          const selfId = socket.id;
          if (selfId) runtime.setRingRequired(selfId, res.data.ringRequired);
        }
      } else {
        setSettings(previous);
        localMedia.applyAudioSettings(previous);
      }
    });
  };

  /** Einstellung merken und sofort an die laufende Ansicht geben. */
  const applyView = (patch: Partial<ViewSettings>): void => {
    updateView(patch);
    runtime.applyViewSettings(useViewStore.getState().settings);
  };

  /**
   * Welcher Stil gerade zu sehen ist. Ohne ausdrueckliche Wahl zeigt die
   * Registry den Standard — dieselbe Rechnung hier, damit die Schaltflaeche
   * die Welt beschreibt und nicht den Speicherplatz.
   */
  const aktiverStil = stil3d ?? STIL3D_STANDARD;

  /**
   * Stil merken und die Ansicht neu aufbauen. Die Reihenfolge ist Pflicht,
   * keine Kuer: `createView` liest die Wahl beim Aufbau aus dem Store.
   */
  const applyStil3d = async (name: string): Promise<void> => {
    if (switching || name === aktiverStil) return;
    const vorher = useViewStore.getState().stil3d;
    setStil3d(name);
    setSwitching(true);
    try {
      await runtime.ansichtNeuAufbauen();
    } catch {
      // Der alte Renderer ist beim Umbau schon freigegeben — zurueck geht es
      // nur ueber einen zweiten Versuch mit der alten Wahl
      setStil3d(vorher);
      await runtime.ansichtNeuAufbauen();
    } finally {
      setSwitching(false);
    }
  };

  /** Regler der Ego-Ansicht; wirkt sofort und liegt nur auf diesem Geraet. */
  const slider = (
    key: 'sensitivity' | 'fieldOfView' | 'quality',
    label: string,
    range: { min: number; max: number },
    step: number,
    format: (value: number) => string,
  ) => (
    <label className="settings-popover__slider">
      <span>
        {label}
        <small>{format(view[key])}</small>
      </span>
      <input
        type="range"
        min={range.min}
        max={range.max}
        step={step}
        value={view[key]}
        onChange={(e) => applyView({ [key]: Number(e.target.value) })}
      />
    </label>
  );

  const viewToggle = (key: 'headBob' | 'ceiling', label: string, hint?: string) => (
    <label className="settings-popover__row">
      <input type="checkbox" checked={view[key]} onChange={(e) => applyView({ [key]: e.target.checked })} />
      <span>
        {label}
        {hint && <small>{hint}</small>}
      </span>
    </label>
  );

  const row = (key: keyof UserSettings, label: string, hint?: string) => (
    <label className="settings-popover__row">
      <input
        type="checkbox"
        checked={settings[key]}
        onChange={(e) => update({ [key]: e.target.checked })}
      />
      <span>
        {label}
        {hint && <small>{hint}</small>}
      </span>
    </label>
  );

  return (
    <div className="settings-popover" ref={ref} role="dialog" aria-label="Einstellungen">
      <h3 className="settings-popover__title">Einstellungen</h3>
      {row(
        'ringRequired',
        'Ich muss angeklingelt werden',
        'Andere müssen erst anklingeln, bevor Audio/Video startet — Raum-Konferenzen ausgenommen.',
      )}
      {row('noiseSuppression', 'Rauschunterdrückung')}
      <details className="settings-popover__advanced">
        <summary>Erweitert</summary>
        {row('echoCancellation', 'Echo-Unterdrückung')}
        {row('autoGainControl', 'Automatische Pegelanpassung')}
      </details>
      {viewMode === '3d' && (
        <>
          <h4 className="settings-popover__subtitle">Ego-Perspektive</h4>
          {stilWaehlbar(mapTheme) && (
          <div className="settings-popover__stil" role="group" aria-label="3D-Stil">
            <span>
              3D-Stil
              {switching && <small>wird neu aufgebaut…</small>}
            </span>
            <div className="settings-popover__stil-optionen">
              {STIL3D_AUSWAHL.map((eintrag) => (
                <button
                  key={eintrag.name}
                  type="button"
                  aria-pressed={aktiverStil === eintrag.name}
                  disabled={switching}
                  onClick={() => void applyStil3d(eintrag.name)}
                >
                  {eintrag.label}
                </button>
              ))}
            </div>
          </div>
          )}
          {/* Mit Fingern gibt es keine Maus — der Regler waere eine Attrappe */}
          {!istTouchGeraet() &&
            slider('sensitivity', 'Mausempfindlichkeit', SENSITIVITY_RANGE, 0.0001, (v) =>
              `${Math.round((v / DEFAULT_VIEW_SETTINGS.sensitivity) * 100)} %`,
            )}
          {slider('fieldOfView', 'Sichtfeld', FIELD_OF_VIEW_RANGE, 1, (v) => `${Math.round(v)}°`)}
          {slider('quality', 'Bildqualität', QUALITY_RANGE, 0.05, (v) => `${Math.round(v * 100)} %`)}
          {viewToggle('headBob', 'Wippen beim Laufen')}
          {viewToggle('ceiling', 'Decke zeigen', 'Aus wirkt heller und übersichtlicher.')}
        </>
      )}
      {links && <nav className="settings-popover__links">{links}</nav>}
    </div>
  );
}
